import { userAPI } from "../UserAPI";
import { useEffect, useId, useState } from "react";

import { useTranslation } from "react-i18next";
import { Search, X } from "lucide-react";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/**
 * Address search backed by the server-side Geoapify autocomplete
 * (/geocode/autocomplete) — the same mechanism tri-app uses. The previous
 * implementation queried Address nodes in the graph DB, which only knows
 * addresses already ingested; the geocoder finds any real address
 * (e.g. "Walther-von-Cronberg-Platz 11, 60594 Frankfurt") including free-text
 * input with postal code and city.
 *
 * Contract (unchanged): `setAddress` receives an object with flat fields
 * { street, streetNumber, postalCode, city, country, lat, lon } on pick,
 * and `null` when the search box is emptied.
 */

interface Properties {
    label?: string;
    setAddress?: (address: any) => void
}

export interface GeocodeSuggestion {
    formatted: string;
    street: string;
    streetNumber: string;
    postalCode: string;
    city: string;
    country: string;
    lat: number;
    lon: number;
}

const suggestionLabel = (suggestion: GeocodeSuggestion): string =>
    suggestion.formatted ||
    `${suggestion.street} ${suggestion.streetNumber || ''}${suggestion.postalCode ? ', ' + suggestion.postalCode + ' ' + suggestion.city : ''}`;

export default function AddressPicker(properties: Properties) {
    const { t } = useTranslation();
    const fieldId = useId();
    const [filter, setFilter] = useState('');
    const [suggestions, setSuggestions] = useState<GeocodeSuggestion[]>([]);
    const [selected, setSelected] = useState<GeocodeSuggestion | null>(null);
    const [menuOpen, setMenuOpen] = useState(false);
    const [searching, setSearching] = useState(false);

    useEffect(() => {
        let cancelled = false;
        const debounce = setTimeout(async () => {
            if (filter && filter.trim().length > 3) {
                setSearching(true);
                setSuggestions([]);

                try {
                    const results = await userAPI.getGeocodeAutocomplete(filter, 8);

                    if (!cancelled) {
                        setSuggestions(results || []);
                        setMenuOpen((results || []).length > 0);
                    }
                } catch (error) {
                    console.warn('Address autocomplete failed >>>', error);
                } finally {
                    if (!cancelled) {
                        setSearching(false);
                    }
                }
            } else {
                setSuggestions([]);
                setMenuOpen(false);
            }
        }, 500);

        return () => {
            cancelled = true;
            clearTimeout(debounce);
        };
    }, [filter]);

    const filterAddresses = async (filter: string | null | undefined) => {
        const normalizedFilter = filter ?? '';
        setFilter(normalizedFilter);
        setSelected(null);
        setSuggestions([]);

        // Do NOT clear parent on every keystroke — that breaks flows where the parent
        // needs the last picked Address until the user picks another (e.g. Objektauswahl + Hinzufügen).
        // Clear only when the search box is emptied.
        if (properties.setAddress && !String(normalizedFilter).trim()) {
            properties.setAddress(null);
        }
    };

    const pickSuggestion = (suggestion: GeocodeSuggestion) => {
        setSelected(suggestion);

        if (properties.setAddress) {
            properties.setAddress(suggestion);
        }

        setMenuOpen(false);
    };

    const fieldValue = selected ? `${selected.street} ${selected.streetNumber || ''}`.trim() : filter;

    return <div className="relative min-w-0">
        <div className="flex min-w-0 flex-col gap-1.5">
            {properties.label
                ?
                <Label htmlFor={fieldId}>{t('addressPicker.addressBuilding')}</Label>
                :
                <></>}
            <div className="relative min-w-0 w-[300px] max-w-full max-sm:w-full">
                <Search aria-hidden="true" className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                    id={fieldId}
                    autoFocus
                    type="search"
                    className="px-8"
                    placeholder={t('addressPicker.enterStreetAndStreetNumber')}
                    value={fieldValue}
                    onChange={(event) => filterAddresses(event.target.value)}
                />
                {fieldValue
                    ?
                    <button
                        type="button"
                        aria-label={t('global.clear')}
                        className="absolute right-1 top-1/2 -translate-y-1/2 rounded-sm p-1 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        onClick={() => filterAddresses('')}
                    >
                        <X className="h-4 w-4" />
                    </button>
                    :
                    null}
                {/* Inline suggestion list — deliberately NOT a portal/Callout: inside a
                    modal (Radix) dialog, portaled content is inert and clicks never
                    register. Rendering in place keeps the picker usable everywhere. */}
                {menuOpen && suggestions.length > 0
                    ?
                    <div className="absolute left-0 top-full z-[1000] mt-1 max-h-64 w-full overflow-y-auto rounded-md border bg-popover text-popover-foreground shadow-md">
                        {suggestions.map((suggestion: GeocodeSuggestion) => (
                            <button
                                type="button"
                                key={suggestionLabel(suggestion)}
                                className="block w-full cursor-pointer px-3 py-2 text-left text-sm hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent focus-visible:outline-none"
                                onMouseDown={(event) => {
                                    // mousedown (not click) so the search field's blur cannot interfere
                                    event.preventDefault();
                                    pickSuggestion(suggestion);
                                }}
                                onKeyDown={(event) => {
                                    // Enter/Space only — the mouse path never reaches click, so nothing fires twice.
                                    if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        pickSuggestion(suggestion);
                                    }
                                }}
                            >
                                {suggestionLabel(suggestion)}
                            </button>
                        ))}
                    </div>
                    :
                    null}
            </div>
        </div>
        {searching
            ?
            <div className="mt-2 text-sm text-muted-foreground">{t('global.searching')}...</div>
            :
            (selected
                ?
                <div className="mt-2 text-sm font-medium">{suggestionLabel(selected)}</div>
                :
                <div className="mt-2 text-sm">&nbsp;</div>
            )}
    </div>
}
